// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {deferUntilModulesLoaded} from '@app/features/platform/utils/DeferUntilModulesLoaded';
import {Logger} from '@app/features/platform/utils/AppLogger';
import ReadStates from '@app/features/read_state/state/ReadStates';
import Dimension from '@app/features/ui/state/Dimension';
import Window from '@app/features/window/state/Window';
import {action, makeAutoObservable, reaction} from 'mobx';

const logger = new Logger('AutoAck');
class AutoAck {
	private readonly windowChannels = new Map<string, Set<string>>();
	private readonly windowConditions = new Map<
		string,
		{
			channelId: string | null;
			isAtBottom: boolean;
			canAutoAck: boolean;
		}
	>();
	constructor() {
		makeAutoObservable<this, 'windowChannels' | 'windowConditions'>(
			this,
			{
				windowChannels: false,
				windowConditions: false,
			},
			{autoBind: true},
		);
		this.setupReactions();
	}
	private setupReactions(): void {
		deferUntilModulesLoaded(() => {
			reaction(
				() => {
					const windowId = Window.windowId;
					const channelId = SelectedChannel.currentChannelId;
					const isWindowFocused = Window.focused;
					if (!channelId) {
						return {windowId, channelId: null, isAtBottom: false, canAutoAck: false};
					}
					const isAtBottom = Dimension.isAtBottom(channelId) ?? false;
					const readState = ReadStates.getIfExists(channelId);
					const isManualAck = readState?.isManualAck ?? false;
					const canAutoAck = !isManualAck && isWindowFocused;
					return {windowId, channelId, isAtBottom, canAutoAck};
				},
				(conditions) => {
					this.updateAutoAckState(conditions);
				},
				{
					name: 'AutoAck.updateAutoAckState',
					fireImmediately: true,
				},
			);
		});
	}
	@action
	private updateAutoAckState(conditions: {
		windowId: string;
		channelId: string | null;
		isAtBottom: boolean;
		canAutoAck: boolean;
	}): void {
		const {windowId, channelId, isAtBottom, canAutoAck} = conditions;
		const prevConditions = this.windowConditions.get(windowId);
		this.windowConditions.set(windowId, {channelId, isAtBottom, canAutoAck});
		if (prevConditions?.channelId && prevConditions.channelId !== channelId) {
			this.disableAutomaticAckInternal(prevConditions.channelId, windowId);
		}
		if (!channelId) {
			return;
		}
		const shouldEnable = isAtBottom && canAutoAck;
		if (shouldEnable) {
			this.enableAutomaticAckInternal(channelId, windowId);
		} else {
			this.disableAutomaticAckInternal(channelId, windowId);
		}
	}
	@action
	private enableAutomaticAckInternal(channelId: string, windowId: string): void {
		const channel = Channels.getChannel(channelId);
		if (channel == null) {
			logger.debug(`Ignoring enableAutomaticAck for non-existent channel ${channelId}`);
			return;
		}
		let channels = this.windowChannels.get(windowId);
		if (channels == null) {
			channels = new Set();
			this.windowChannels.set(windowId, channels);
		}
		if (!channels.has(channelId)) {
			channels.add(channelId);
			logger.debug(`Enabled automatic ack for ${channelId} in window ${windowId}`);
		}
	}
	@action
	private disableAutomaticAckInternal(channelId: string, windowId: string): void {
		const channels = this.windowChannels.get(windowId);
		if (channels == null) return;
		if (channels.has(channelId)) {
			channels.delete(channelId);
			logger.debug(`Disabled automatic ack for ${channelId} in window ${windowId}`);
		}
		if (channels.size === 0) {
			this.windowChannels.delete(windowId);
		}
	}
	isAutomaticAckEnabled(channelId: string): boolean {
		for (const channels of this.windowChannels.values()) {
			if (channels.has(channelId)) return true;
		}
		return false;
	}
	@action
	disableForChannel(channelId: string): void {
		for (const [windowId, channels] of this.windowChannels.entries()) {
			if (channels.has(channelId)) {
				channels.delete(channelId);
				logger.debug(`Force-disabled automatic ack for ${channelId} in window ${windowId}`);
			}
			if (channels.size === 0) {
				this.windowChannels.delete(windowId);
			}
		}
	}
}
export default new AutoAck();
